import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../Login/AuthContext';
import { useTranslation } from 'react-i18next';
import Navbar from '../UI/Navbar';
import api from '../../api';
import './PricingPage.css';
import { stripeConfig } from '../../config/stripeConfig';
import { Helmet } from 'react-helmet';

const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const { t, i18n } = useTranslation('pricing');
  const currentLang = i18n.language;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCredits, setSelectedCredits] = useState(stripeConfig.oneTime[0]?.id || '');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  // Check if the user already has an active subscription
  useEffect(() => {
    if (isAuthenticated) {
      setCheckingSubscription(true);
      api.get('/check_subscription')
        .then(response => {
          setHasActiveSubscription(response.data.hasActiveSubscription);
        })
        .catch(err => {
          console.error('Error checking subscription status:', err);
        })
        .finally(() => {
          setCheckingSubscription(false);
        });
    } else {
      setCheckingSubscription(false);
    }
  }, [isAuthenticated]);

  // Function to get the selected package
  const getSelectedCreditOption = () => {
    return stripeConfig.oneTime.find(option => option.id === selectedCredits) || stripeConfig.oneTime[0];
  };

  const handleOneTimePurchase = async () => {
    if (!isAuthenticated) {
      navigate(`/${currentLang === 'fr' ? 'fr/' : ''}login`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const creditOption = getSelectedCreditOption();
      
      const response = await api.post(`/checkout/one-time/${creditOption.id}`, {
        tokens: creditOption.tokens
      });
      
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error initiating one-time checkout:', err);
      setError("An error occurred while initializing the payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionPurchase = async () => {
    if (!isAuthenticated) {
      navigate(`/${currentLang === 'fr' ? 'fr/' : ''}login`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/checkout/subscription/${stripeConfig.subscription.id}`, {
        tokens: stripeConfig.subscription.tokens
      });
      
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Error initiating subscription checkout:', err);
      setError("An error occurred while initializing the subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderSubscriptionButton = () => {
    if (checkingSubscription) {
      return <button className="btn btn-featured" disabled>Loading...</button>;
    }
    
    if (hasActiveSubscription) {
      return (
        <div className="subscription-active-message">
          <p>{t('subscription.activeMessage')}</p>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setLoading(true);
              api.get('/stripe_dashboard')
                .then(response => {
                  window.location.href = response.data.dashboard_url;
                })
                .catch(err => {
                  console.error('Error opening Stripe dashboard:', err);
                  setError('Failed to open subscription management dashboard');
                })
                .finally(() => {
                  setLoading(false);
                });
            }}
            disabled={loading}
          >
            {loading ? 'Loading...' : t('subscription.manageButton')}
          </button>
        </div>
      );
    }
    
    return (
      <button 
        className="btn btn-featured" 
        onClick={handleSubscriptionPurchase}
        disabled={loading}
      >
        {loading ? 'Loading...' : isAuthenticated ? t('subscription.button') : t('auth.loginToSubscribe')}
      </button>
    );
  };

  return (
    <div className="pricing-page-container">
      <Navbar />
      
      <Helmet>
        <title>{t('seo.title')}</title>
        <meta name="description" content={t('seo.description')} />
        <link rel="canonical" href={`https://aivatar.muware.xyz${currentLang === 'fr' ? '/fr' : ''}/pricing`} />
        
        {/* Alternate language versions for hreflang */}
        <link
          rel="alternate"
          href="https://aivatar.muware.xyz/pricing"
          hrefLang="en"
        />
        <link
          rel="alternate"
          href="https://aivatar.muware.xyz/fr/pricing"
          hrefLang="fr"
        />
      </Helmet>
      
      <div className="pricing-page">
        <div className="pricing-header">
          <h1>{t('header.title')}</h1>
          <p>{t('header.subtitle')}</p>
        </div>

        <div className="pricing-container">
          {/* One-time payment card */}
          <div className="pricing-card">
            <div className="pricing-card-header">
              <h2>{t('oneTime.title')}</h2>
              <div className="pricing-price">
                <span className="currency">€</span>
                <span className="amount">{getSelectedCreditOption().price.toString().split('.')[0]}</span>
                <span className="period">,{getSelectedCreditOption().price.toString().split('.')[1] || '00'}</span>
              </div>
              <p className="pricing-description">{t('oneTime.description')}</p>
            </div>
            
            <div className="pricing-features">
              <div className="credit-selector">
                <label htmlFor="credit-pack">{t('oneTime.choosePack')}</label>
                <select 
                  id="credit-pack" 
                  value={selectedCredits}
                  onChange={(e) => setSelectedCredits(e.target.value)}
                >
                  {stripeConfig.oneTime.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.tokens} {t('oneTime.features.credits')}
                    </option>
                  ))}
                </select>
              </div>
              <p className="pricing-tagline">
                {t('oneTime.tagline')}
              </p>
              <ul>
                <li>✓ {getSelectedCreditOption().tokens} {t('oneTime.features.credits')}</li>
                <li>✓ {t('oneTime.features.expire')}</li>
              </ul>
            </div>
            
            <div className="pricing-action">
              <button 
                className="btn btn-primary" 
                onClick={handleOneTimePurchase}
                disabled={loading}
              >
                {loading ? 'Loading...' : isAuthenticated ? t('oneTime.button') : t('auth.loginToBuy')}
              </button>
            </div>
          </div>

          {/* Subscription card */}
          <div className="pricing-card featured">
            <div className="featured-badge">{t('subscription.badge')}</div>
            <div className="pricing-card-header">
              <h2>{t('subscription.title')}</h2>
              <div className="pricing-price">
                <span className="currency">€</span>
                <span className="amount">{stripeConfig.subscription.price.toString().split('.')[0]}</span>
                <span className="period">,{stripeConfig.subscription.price.toString().split('.')[1] || '00'}</span>
                <span className="billing-period">{t('price.perMonth')}</span>
              </div>
              <p className="pricing-description">{t('subscription.description', {tokens: stripeConfig.subscription.tokens})}</p>
            </div>
            
            <div className="pricing-features">
              <ul>
                <li>✓ {stripeConfig.subscription.tokens} {t('subscription.features.tokens')}</li>
                <li>✓ {t('subscription.features.styles')}</li>
                <li>✓ {t('subscription.features.generators')}</li>
                <li>✓ {t('subscription.features.templates')}</li>
                <li>✓ {t('subscription.features.cancel')}</li>
              </ul>
            </div>
            
            <div className="pricing-action">
              {renderSubscriptionButton()}
            </div>
          </div>
        </div>

        {error && <p className="pricing-error">{error}</p>}

        <div className="pricing-faq">
          <h2>{t('faq.title')}</h2>
          
          <div className="faq-item">
            <h3>{t('faq.items.difference.q')}</h3>
            <p>{t('faq.items.difference.a', {tokens: stripeConfig.subscription.tokens})}</p>
          </div>
          
          <div className="faq-item">
            <h3>{t('faq.items.cancel.q')}</h3>
            <p>{t('faq.items.cancel.a')}</p>
          </div>
          
          <div className="faq-item">
            <h3>{t('faq.items.hidden.q')}</h3>
            <p>{t('faq.items.hidden.a')}</p>
          </div>

          <div className="faq-item">
            <h3>{t('faq.items.expire.q')}</h3>
            <p>{t('faq.items.expire.a')}</p>
          </div>
        </div>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "AiVatar - AI Avatar & Headshot Generator",
            description: t('seo.description'),
            image: "https://aivatar.muware.xyz/og-image.webp",
            brand: {
              "@type": "Brand",
              name: "AiVatar"
            },
            offers: [
              {
                "@type": "Offer",
                price: "9",
                priceCurrency: "EUR",
                priceValidUntil: "2025-12-31",
                availability: "https://schema.org/InStock",
                name: "40-token lifetime pack"
              },
              {
                "@type": "Offer",
                price: "19",
                priceCurrency: "EUR",
                priceValidUntil: "2025-12-31",
                availability: "https://schema.org/InStock",
                name: "100-token lifetime pack"
              },
              {
                "@type": "Offer",
                price: "9",
                priceCurrency: "EUR",
                priceValidUntil: "2025-12-31",
                availability: "https://schema.org/InStock",
                name: "Monthly subscription - 100 tokens/month",
                eligibleDuration: "P1M"
              }
            ]
          })}
        </script>
      </div>
    </div>
  );
};

export default PricingPage; 